import React, { useCallback, useEffect } from 'react'
import {
  PiGearFill,
  PiTrayFill,
  PiChartLineUpFill,
  PiMegaphoneFill,
} from 'react-icons/pi'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavItem } from './SlideOutNavItem'
import { TargetsSlideOutContent } from './TargetsSlideOutContent'
import { WorkspaceSlideOutContent } from './WorkspaceSlideOutContent'
import { SettingsSlideOutContent } from './SettingsSlideOutContent'
import { TargetArrowIcon } from './nav-icons'
import { cn } from 'components/UI/cn'
import type { NavSection } from './types'

// Section titles for the slide-out panel
const SECTION_TITLES: Record<NavSection, string> = {
  targets: 'Targets',
  campaigns: 'Campaigns',
  samples: 'Samples',
  rebates: 'Rebates',
  deals: 'Deals',
  performance: 'Performance',
  workspace: 'Workspace',
  settings: 'Settings',
}

// Section configurations for non-targets sections
// Note: 'settings' is handled dynamically by SettingsSlideOutContent
// Note: 'workspace' is handled dynamically by WorkspaceSlideOutContent
const SECTION_ITEMS: Partial<
  Record<NavSection, Array<{ label: string; path: string }>>
> = {
  performance: [{ label: 'Dashboard', path: '/dashboard' }],
  campaigns: [{ label: 'All Campaigns', path: '/campaigns' }],
}

interface SlideOutPanelProps {
  className?: string
  $isOpen?: boolean
}

export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({
  className,
  $isOpen = false,
}) => {
  const { isSlideOutOpen, activeSection, closeSlideOut } = useLeftNavStore()

  const title = activeSection ? SECTION_TITLES[activeSection] : ''
  const items = activeSection ? SECTION_ITEMS[activeSection] : undefined

  // Close on escape key
  useEffect(() => {
    if (!isSlideOutOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSlideOut()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSlideOutOpen, closeSlideOut])

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (
      !target.closest('[data-slide-out-panel]') &&
      !target.closest('[data-left-nav]')
    ) {
      closeSlideOut()
    }
  }, [])

  useEffect(() => {
    if (!isSlideOutOpen) return

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSlideOutOpen, handleClickOutside])

  // Render the appropriate content based on active section
  const renderContent = () => {
    if (activeSection === 'targets') {
      return <TargetsSlideOutContent />
    }

    // Settings uses dynamic content with feature flags and permissions
    if (activeSection === 'settings') {
      return <SettingsSlideOutContent />
    }

    // Workspace uses dynamic content with feature flags
    if (activeSection === 'workspace') {
      return <WorkspaceSlideOutContent />
    }

    if (items && activeSection) {
      return (
        <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
          {items.map((item) => (
            <SlideOutNavItem
              key={item.path}
              label={item.label}
              path={item.path}
              section={activeSection}
            />
          ))}
        </ul>
      )
    }

    return null
  }

  return (
    <aside
      className={cn(
        'bg-[var(--slide-out-bg)] transition-all duration-200 ease-in-out flex flex-col rounded-l-lg overflow-hidden',
        $isOpen
          ? 'w-[var(--slide-out-panel-width)] min-w-[var(--slide-out-panel-width)]'
          : 'w-0 min-w-0',
        className
      )}
      role="navigation"
      aria-label={`${title} navigation`}
      data-slide-out-panel
    >
      <div className="w-[var(--slide-out-panel-width)] flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-1 p-3 opacity-80">
          <div className="flex items-center justify-center p-1 text-[#191d23] [&_svg]:w-6 [&_svg]:h-6">
            {activeSection === 'targets' && <TargetArrowIcon size={24} />}
            {activeSection === 'performance' && <PiChartLineUpFill />}
            {activeSection === 'campaigns' && <PiMegaphoneFill />}
            {activeSection === 'workspace' && <PiTrayFill />}
            {activeSection === 'settings' && <PiGearFill />}
          </div>
          <h2 className="m-0 flex items-center text-md font-normal leading-[1.5rem] text-[#191d23] uppercase tracking-[0.05em]">
            {title}
          </h2>
        </div>

        {/* Divider */}
        <div className="h-px bg-[rgba(25,29,35,0.15)] mx-3 mb-3" />

        {/* Content */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {renderContent()}
        </nav>
      </div>
    </aside>
  )
}
